import { Box, NavList } from '@deckee/deck-hand';
import React, { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  getAllSignalsFollowedByTheCurrentMember,
  getAllSignalsSharedWithCurrentMember,
} from '../../../../services/store/modules/signal';
import { formatDistanceDisplay } from '../../../helpers/data-helpers';
import SheetLayout, {
  SheetBody,
} from '../../../layouts/SheetLayout/SheetLayout';
import UserShareItem from '../components/UserShareItem/UserShareItem';
import usePublicSignals from '../hooks/use-public-signals';
import useSignalUtilities from '../hooks/use-signal-utilities';

const FriendsPageHeading = styled(Box).attrs({
  fontSize: 0,
  color: 'darkergrey',
  mt: 3,
  mb: 2,
  pl: 1,
  textAlign: 'left',
})`
  text-transform: uppercase;
`;

const formatDistanceFromMember = (distanceFromMember?: number) => {
  if (!distanceFromMember) return '';

  const distanceInNm = (
    formatDistanceDisplay(distanceFromMember, {
      distance: 'nm',
    }) as { value: number }
  )?.value;

  return `${distanceInNm.toFixed(0)}nm`;
};

const FriendsPage: FC = () => {
  const { publicSignals: allPublicSignals } = usePublicSignals();

  const followedSignals = useSelector(getAllSignalsFollowedByTheCurrentMember);

  const safetyContacts = useSelector(getAllSignalsSharedWithCurrentMember);
  const communitySignals = [...followedSignals, ...allPublicSignals].filter(
    (signal) => !safetyContacts.find((sc) => sc.id === signal.id),
  );

  const { navigateToSignal, centerMapOnSignal } = useSignalUtilities();

  const ref = useRef<HTMLDivElement>(null);

  return (
    <SheetLayout showMenuBar={false} heading="On the water">
      <SheetBody maxHeight="40vh" overflowY="auto" ref={ref}>

        {safetyContacts.length > 0 && (
          <>
            <FriendsPageHeading key="heading">
              You are a safety contact
            </FriendsPageHeading>
            {safetyContacts.map((signal) => (
              <UserShareItem
                key={signal.id}
                padded
                displayName={signal.username}
                memberId={signal.memberid}
                userImageId={signal.image || ''}
                timestamp={signal?.timestamp}
                subscriber={signal.subscribed}
                distanceFromMember={formatDistanceFromMember(
                  signal.distance_from_member,
                )}
                // Need to have the share id returned from the server :shrug
                itemAction={() => navigateToSignal(signal.id)}
              />
            ))}
          </>
        )}

        {communitySignals.length > 0 && (
          <>
            <FriendsPageHeading key="heading">Community</FriendsPageHeading>
            <NavList key="list">
              {communitySignals.map((signal) => (
                <UserShareItem
                  key={signal.id}
                  padded
                  displayName={signal.username}
                  memberId={signal.memberid}
                  userImageId={signal.image || ''}
                  timestamp={signal?.timestamp}
                  subscriber={signal.subscribed}
                  distanceFromMember={formatDistanceFromMember(
                    signal.distance_from_member,
                  )}
                  // Need to have the share id returned from the server :shrug
                  itemAction={() => centerMapOnSignal(signal.id, ref?.current)}
                />
              ))}
            </NavList>
          </>
        )}
      </SheetBody>
    </SheetLayout>
  );
};

export default FriendsPage;
